import { inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, catchError, map, of, switchMap } from 'rxjs';

import { MemberProfilesService } from '@hm/core/services/member-profiles.service';
import { VisitsService } from '@hm/core/services/visits.service';
import * as MemberHubActions from '@hm/core/stores/member-hub/member-hub.actions';

import { VisitStatusTypes } from '@lib/core/models/visit.interface';

export const fetchMemberProfileEffect = createEffect(
  (actions$ = inject(Actions), memberProfilesService = inject(MemberProfilesService)): Observable<Action> =>
    actions$.pipe(
      ofType(MemberHubActions.fetchMemberProfile),
      switchMap(action =>
        memberProfilesService.getClientProfile(action.memberId).pipe(
          map(memberProfile => MemberHubActions.fetchMemberProfileSuccess({ memberProfile })),
          catchError(() => of(MemberHubActions.fetchMemberProfileFailure({ error: 'Failed to fetch member profile' })))
        )
      )
    ),
  { functional: true }
);

export const fetchMemberFranchiseEffect = createEffect(
  (actions$ = inject(Actions), memberProfilesService = inject(MemberProfilesService)): Observable<Action> =>
    actions$.pipe(
      ofType(MemberHubActions.fetchMemberFranchise),
      switchMap(action =>
        memberProfilesService.getPackageFranchisesByMemberId(action.memberId).pipe(
          map(memberPackage => MemberHubActions.fetchMemberFranchiseSuccess({ franchises: memberPackage })),
          catchError(() => of(MemberHubActions.fetchMemberFranchiseFailure({ error: 'Failed to fetch member franchise' })))
        )
      )
    ),
  { functional: true }
);

export const fetchMemberPackageEffect = createEffect(
  (actions$ = inject(Actions), memberProfilesService = inject(MemberProfilesService)): Observable<Action> =>
    actions$.pipe(
      ofType(MemberHubActions.fetchMemberPackage),
      switchMap(action =>
        memberProfilesService.getPackageByUserId(action.userId).pipe(
          map(memberPackage => MemberHubActions.fetchMemberPackageSuccess({ memberPackage: memberPackage })),
          catchError(() => of(MemberHubActions.fetchMemberPackageFailure({ error: 'Failed to fetch member package' })))
        )
      )
    ),
  { functional: true }
);

export const fetchMemberAppointmentsEffect = createEffect(
  (actions$ = inject(Actions), visitsService = inject(VisitsService)): Observable<Action> =>
    actions$.pipe(
      ofType(MemberHubActions.fetchMemberAppointments),
      switchMap(action =>
        visitsService
          .searchAllVisits({
            userId: action.userId,
            page: 1,
            pageSize: 20,
            status: VisitStatusTypes.Done,
            sortBy: 'creationDate',
          })
          .pipe(
            map(response => MemberHubActions.fetchMemberAppointmentsSuccess({ appointments: response.value || [] })),
            catchError(() => of(MemberHubActions.fetchMemberAppointmentsFailure({ error: 'Failed to fetch member appointments' })))
          )
      )
    ),
  { functional: true }
);
